"use client";

import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function PlaygroundPage() {
  const [activeTab, setActiveTab] = useState<"jwt" | "docker" | "uuid">("jwt");

  // JWT Decoder States
  const [jwtInput, setJwtInput] = useState("");
  const [jwtHeader, setJwtHeader] = useState("");
  const [jwtPayload, setJwtPayload] = useState("");
  const [jwtError, setJwtError] = useState("");

  const handleDecodeJwt = (token: string) => {
    setJwtInput(token);
    if (!token.trim()) {
      setJwtHeader("");
      setJwtPayload("");
      setJwtError("");
      return;
    }
    const parts = token.split(".");
    if (parts.length !== 3) {
      setJwtError("Invalid Token: Must have header, payload, and signature parts.");
      return;
    }
    try {
      setJwtError("");
      const headerDecoded = atob(parts[0].replace(/-/g, "+").replace(/_/g, "/"));
      const payloadDecoded = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
      setJwtHeader(JSON.stringify(JSON.parse(headerDecoded), null, 2));
      setJwtPayload(JSON.stringify(JSON.parse(payloadDecoded), null, 2));
    } catch {
      setJwtError("Fail to base64-decode: String contains invalid characters.");
    }
  };

  // Docker Compose Generator States
  const [dbName, setDbName] = useState("my_database");
  const [dbPass, setDbPass] = useState("secretpassword");
  const [composeOutput, setComposeOutput] = useState("");

  const generateCompose = () => {
    const yaml = `version: "3.8"

services:
  web:
    image: node:20-alpine
    container_name: nextjs_web_node
    restart: always
    ports:
      - "3000:3000"
    environment:
      - DATABASE_URL=postgresql://postgres:${dbPass}@db:5432/${dbName}?schema=public
    depends_on:
      - db

  db:
    image: postgres:15-alpine
    container_name: postgres_db_node
    restart: always
    environment:
      - POSTGRES_DB=${dbName}
      - POSTGRES_PASSWORD=${dbPass}
    ports:
      - "5432:5432"
    volumes:
      - postgres_data:/var/lib/postgresql/data

volumes:
  postgres_data:`;
    setComposeOutput(yaml);
  };

  // UUID & Password Generator States
  const [uuidList, setUuidList] = useState<string[]>([]);
  const [passwordOutput, setPasswordOutput] = useState("");

  const generateUuid = () => {
    const list = Array.from({ length: 3 }, () => {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    });
    setUuidList(list);
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+";
    const pass = Array.from({ length: 16 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    setPasswordOutput(pass);
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-32 pb-20 relative z-10 font-[var(--font-space-grotesk)] text-gray-300">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.04)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] -z-10 opacity-60" />
        
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-16 text-center">
            <span className="text-xs font-bold tracking-[0.4em] text-indigo-400 uppercase mb-4 block font-mono">
              Laboratory
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-white font-[var(--font-poppins)] tracking-tighter mb-6">
              Developer <span className="gradient-text">Playground</span>
            </h1>
            <p className="text-gray-400 text-xs sm:text-sm font-medium max-w-xl mx-auto">
              Interactive utility modules designed client-side. Parse authorization claims, template Docker compose YAML stacks, or generate cryptographically stable parameters.
            </p>
          </div>

          {/* Navigation tabs */}
          <div className="flex justify-center gap-3 mb-10">
            {[
              { id: "jwt", label: "JWT Decoder" },
              { id: "docker", label: "Docker Generator" },
              { id: "uuid", label: "Keys Generator" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as "jwt" | "docker" | "uuid")}
                className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all duration-300 cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-indigo-500/15 border-indigo-400 text-white shadow-xl shadow-indigo-500/10"
                    : "bg-white/5 border-white/5 text-gray-400 hover:border-white/10 hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="bg-[#05030e]/40 border border-white/5 rounded-[2rem] p-8 md:p-10 shadow-2xl">
            {/* JWT Decoder Panel */}
            {activeTab === "jwt" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2 font-mono">Input JSON Web Token</h3>
                  <textarea
                    value={jwtInput}
                    onChange={(e) => handleDecodeJwt(e.target.value)}
                    placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkFtcml0IFNoYXJtYSIsImFkbWluIjp0cnVlfQ..."
                    className="w-full h-24 p-4 rounded-xl bg-black/60 border border-white/5 font-mono text-[10px] text-gray-300 focus:outline-none focus:border-indigo-500/40"
                  />
                </div>

                {jwtError && (
                  <div className="p-3 rounded-lg border border-red-500/20 bg-red-500/5 text-xs text-red-400 font-mono">
                    {jwtError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-2 font-mono">Decoded Header</h4>
                    <pre className="p-4 h-48 rounded-xl bg-black/60 border border-white/5 overflow-auto text-[10px] font-mono text-emerald-400 leading-normal">
                      {jwtHeader || "{\n  // Header data will appear here\n}"}
                    </pre>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-2 font-mono">Decoded Claims</h4>
                    <pre className="p-4 h-48 rounded-xl bg-black/60 border border-white/5 overflow-auto text-[10px] font-mono text-indigo-300 leading-normal">
                      {jwtPayload || "{\n  // Claims data will appear here\n}"}
                    </pre>
                  </div>
                </div>
              </div>
            )}

            {/* Docker Compose Generator Panel */}
            {activeTab === "docker" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-bold text-white uppercase tracking-wider mb-2 block font-mono">PostgreSQL Database Name</label>
                    <input
                      type="text"
                      value={dbName}
                      onChange={(e) => setDbName(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl bg-black/60 border border-white/5 font-mono text-xs text-gray-300 focus:outline-none focus:border-indigo-500/40"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-white uppercase tracking-wider mb-2 block font-mono">PostgreSQL Password</label>
                    <input
                      type="text"
                      value={dbPass}
                      onChange={(e) => setDbPass(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl bg-black/60 border border-white/5 font-mono text-xs text-gray-300 focus:outline-none focus:border-indigo-500/40"
                    />
                  </div>
                </div>

                <button
                  onClick={generateCompose}
                  className="btn-gradient w-full py-3 rounded-xl text-xs font-bold uppercase tracking-widest text-white shadow-xl shadow-indigo-500/10 cursor-pointer"
                >
                  Generate docker-compose.yml
                </button>

                {composeOutput && (
                  <div>
                    <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-2 font-mono">Compose Manifest</h4>
                    <pre className="p-4 rounded-xl bg-black/60 border border-white/5 overflow-x-auto text-[10px] font-mono text-indigo-300 leading-normal">
                      {composeOutput}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* UUID & Password Generator Panel */}
            {activeTab === "uuid" && (
              <div className="space-y-8">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">Secure UUID Generator</h3>
                    <button
                      onClick={generateUuid}
                      className="px-4 py-1.5 rounded-lg border border-white/10 bg-white/5 text-[10px] font-mono font-bold uppercase tracking-widest text-white hover:bg-white/10 transition-colors cursor-pointer"
                    >
                      Generate UUID v4
                    </button>
                  </div>
                  {uuidList.length > 0 && (
                    <ul className="space-y-2 bg-black/40 border border-white/5 p-4 rounded-xl font-mono text-[10px] text-gray-400">
                      {uuidList.map((uuid, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-indigo-400 font-bold shrink-0">uuid_{i + 1}:</span>
                          <span>{uuid}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="h-px bg-white/5" />

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">Random Token Generator</h3>
                    <button
                      onClick={generatePassword}
                      className="px-4 py-1.5 rounded-lg border border-white/10 bg-white/5 text-[10px] font-mono font-bold uppercase tracking-widest text-white hover:bg-white/10 transition-colors cursor-pointer"
                    >
                      Generate Key
                    </button>
                  </div>
                  {passwordOutput && (
                    <pre className="p-4 rounded-xl bg-black/60 border border-white/5 text-[11px] font-mono text-emerald-400 leading-normal">
                      {passwordOutput}
                    </pre>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
